import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  useAdminGetAgencies,
  useAdminGetAgencyRuns,
  useAdminGetListedAgencies,
} from "../../api/hooks/admin";
import { Page, Spinner } from "../../components";
import { styles, runsTableColumnWidths } from "./styles";
import Select from "react-select";
import CreateAccessTokenModal from "../../components/CreateAccessTokenModal";
import { AGENCY_STATUSES, Agency, AgencyStatus } from "../../api/admin/types";
import Button from "../../components/Button";
import InviteUsersModal from "../../components/InviteUsersModal";
import ConfirmDeleteUserModal from "../../components/ConfirmModals/ConfirmDeleteUserModal";
import Trash from "../../assets/icons/Trash.svg";
import EditableTableText from "../../components/EditableTableText";
import { handleAdminLoginAsUser } from "../../utils/adminLoginAsUser";
import LogoutButton from "../../components/LogoutButton";
import { escapeCsvField } from "../../utils/csv";
import "./responsive.css";

// --- CSV helpers ---
function arrayToCSV(data: any[], columns: { key: string; label: string }[]) {
  const header = columns.map((col) => `"${col.label}"`).join(",");
  const rows = data.map((row) => columns.map((col) => escapeCsvField(row[col.key])).join(","));
  return [header, ...rows].join("\r\n");
}

// A run's `instanceIds` field is a plain string: either a single instance id,
// or multiple ids joined by commas. Split it to get the real instance count.
function getInstanceCount(instanceIds: string | null | undefined): number {
  if (!instanceIds) return 0;
  return instanceIds.split(",").filter((id) => id.trim().length > 0).length;
}

function getRunTaskTypeLabel(instanceIds: string | null | undefined): string {
  return getInstanceCount(instanceIds) > 1 ? "Comparison" : "Analysis";
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// --- Constants ---
const columns = [
  { key: "index", label: "Index" },
  { key: "generationTime", label: "Generation Time" },
  { key: "status", label: "Status" },
  { key: "taskType", label: "Task Type" },
  { key: "username", label: "User" },
  { key: "email", label: "Email" },
];

type UserToDeleteType = {
  user_id: string;
  team_name: string;
};

const PAGE_SIZE = 20;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Use a portal for dropdown menus to avoid clipping in scrollable/overflow-hidden containers
const selectMenuPortalTarget = typeof document !== "undefined" ? document.body : undefined;

// --- Main Component ---
const AgencyPage: React.FC = () => {
  // --- State ---
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [filter, setFilter] = useState(false);
  const [totalsMonth, setTotalsMonth] = useState("all");
  const [showCreateTokenModal, setShowCreateTokenModal] = useState(false);
  const [showInviteUsersModal, setShowInviteUsersModal] = useState(false);
  const [showConfirmDeleteUserModal, setShowConfirmDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserToDeleteType | null>(null);
  const [page, setPage] = useState(1);
  const [agency, setAgency] = useState<Agency | null>(null);
  const [agencyUsersInfo, setAgencyUsersInfo] = useState<any>(null);
  const [isLoadingUsersInfo, setIsLoadingUsersInfo] = useState(false);
  const [adminUpdatingUserId, setAdminUpdatingUserId] = useState<string | null>(null);
  const [adminUpdatingAgency, setAdminUpdatingAgency] = useState(false);
  const [isActivityTable, setIsActivityTable] = useState(true);
  const [editingSettingsField, setEditingSettingsField] = useState<string | null>(null);
  const [testAccountOverrides, setTestAccountOverrides] = useState<Record<string, boolean | null>>(
    {}
  );

  // --- Hooks ---
  const { agencyId } = useParams();
  const { data: agencies } = useAdminGetAgencies(agencyId);
  // Move-target picker: every agency on the onboarding allowlist.
  const { data: listedAgencies } = useAdminGetListedAgencies();
  const agencyOptions = useMemo(
    () => (listedAgencies ?? []).map((a) => ({ value: a.agencyId, label: a.name || a.agencyId })),
    [listedAgencies]
  );
  const { data: runsTotal, isLoading, isError } = useAdminGetAgencyRuns(agencyId!);
  const navigate = useNavigate();
  const location = useLocation();
  const agencyName = location.state?.name;

  // --- Effects ---
  useEffect(() => {
    if (agencies) setAgency(agencies[0]);
  }, [agencies]);

  // --- Agency Users Info ---
  // Tracks the most recently issued fetchAgencyUsersInfo call so an in-flight
  // fetch can tell, once it resolves, whether a newer request has since been
  // issued (see `fetchAgencyUsersInfo` below). A monotonic counter (rather
  // than the requested month) is required because two requests for the same
  // month can also be in flight at once (e.g. delete-user and create-team
  // both refetch with the current month), and must still resolve in order.
  const latestRequestIdRef = React.useRef(0);

  const fetchAgencyUsersInfo = useCallback(
    async (month: string) => {
      if (!agencyId) return;
      // Staleness guard: if a newer fetchAgencyUsersInfo call is issued
      // before this one resolves, discard this response instead of
      // overwriting newer data.
      const requestId = ++latestRequestIdRef.current;
      setIsLoadingUsersInfo(true);
      try {
        const selectedMonth = month === "all" ? null : month;
        const resp = await (
          await import("../../api/admin/adminApi")
        ).default.adminGetAgencyUsersInfo(agencyId, selectedMonth);
        if (requestId !== latestRequestIdRef.current) return;
        const body = resp.body;
        const normalized =
          body && body.teams_list
            ? {
                ...body,
                teams_list: body.teams_list.map((teamObj: any) => {
                  const teamId = Object.keys(teamObj)[0];
                  const team = teamObj[teamId] ?? {};
                  const users = Array.isArray(team.users_info) ? team.users_info : [];
                  const normalizedUsers = users.map((u: any) => ({
                    ...u,
                    is_test_account: Object.prototype.hasOwnProperty.call(u, "is_test_account")
                      ? u.is_test_account
                      : null,
                  }));
                  return {
                    [teamId]: {
                      ...team,
                      users_info: normalizedUsers,
                    },
                  };
                }),
              }
            : body;
        setAgencyUsersInfo(normalized);
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoadingUsersInfo(false);
        }
      }
    },
    [agencyId]
  );

  useEffect(() => {
    fetchAgencyUsersInfo(totalsMonth);
  }, [fetchAgencyUsersInfo, totalsMonth]);

  // --- Teams ---
  const teamsList = agencyUsersInfo?.teams_list ?? [];
  const teamIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    teamsList.forEach((teamObj) => {
      const teamId = Object.keys(teamObj)[0];
      const teamName = teamObj[teamId]?.team_name ?? teamId;
      map[teamId] = teamName;
    });
    return map;
  }, [teamsList]);
  const teamIds = Object.keys(teamIdToName);

  const [selectedTeamId, setSelectedTeamId] = useState<string>("all_teams");
  useEffect(() => {
    if (teamIds.length > 0 && selectedTeamId !== "all_teams" && !teamIds.includes(selectedTeamId)) {
      setSelectedTeamId("all_teams");
    }
  }, [teamIds.join(","), selectedTeamId]);

  const selectedUsersRaw =
    selectedTeamId === "all_teams"
      ? teamsList.flatMap((teamObj) => {
          const teamId = Object.keys(teamObj)[0];
          return teamObj[teamId]?.users_info ?? [];
        })
      : (teamsList.find((teamObj) => Object.keys(teamObj)[0] === selectedTeamId)?.[selectedTeamId]
          ?.users_info ?? []);

  const selectedUsers = useMemo(() => {
    const seen = new Set<string>();
    return selectedUsersRaw.filter((user) => {
      const key = user.user_id || `email:${user.email}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [selectedUsersRaw]);

  // --- Runs ---
  const filteredRuns = useMemo(() => {
    if (!runsTotal?.runs) return [];
    if (!filter) return runsTotal?.runs;
    return runsTotal?.runs.filter((r) => getInstanceCount(r.instanceIds) > 1);
  }, [runsTotal, filter]);

  const totalPages = Math.ceil(filteredRuns.length / PAGE_SIZE) || 1;
  const pagedRuns = useMemo(
    () => filteredRuns.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRuns, page]
  );

  // --- Agency Update Helpers ---
  const statusLabel = (s: AgencyStatus) => s.charAt(0).toUpperCase() + s.slice(1);
  const displayStatus: AgencyStatus = agency?.status ?? (agency?.isActive ? "active" : "inactive");

  const getAgencyUpdateData = (changedField: string, changedValue: any) => ({
    status: changedField === "status" ? changedValue : (agency?.status ?? undefined),
    monthly_credits:
      changedField === "monthly_credits"
        ? changedValue
        : agency?.monthlyCredits !== undefined && agency?.monthlyCredits !== null
          ? String(agency.monthlyCredits)
          : null,
    monthly_fee:
      changedField === "monthly_fee"
        ? changedValue
        : agency?.monthlyFee !== undefined && agency?.monthlyFee !== null
          ? String(agency.monthlyFee)
          : null,
    overage_fee:
      changedField === "overage_fee"
        ? changedValue
        : agency?.overageFee !== undefined && agency?.overageFee !== null
          ? String(agency.overageFee)
          : null,
    plan_type:
      changedField === "plan_type" ? changedValue : agency?.planType ? agency.planType : null,
  });

  const handleChangeStatus = async (newStatus: AgencyStatus) => {
    if (!agencyId || !agency) return;
    setAdminUpdatingAgency(true);
    try {
      const adminApi = (await import("../../api/admin/adminApi")).default;
      const data = getAgencyUpdateData("status", newStatus);
      const resp = await adminApi.adminUpdateAgency(agencyId, data);
      if (resp?.status === 200) {
        setAgency((prev) =>
          prev ? { ...prev, status: newStatus, isActive: newStatus !== "inactive" } : prev
        );
      }
    } finally {
      setAdminUpdatingAgency(false);
    }
  };

  const handleAgencyFieldEdit = async (
    field: "monthly_fee" | "overage_fee" | "monthly_credits" | "plan_type",
    value: any
  ) => {
    if (!agencyId || !agency) return;
    setAdminUpdatingAgency(true);
    try {
      const adminApi = (await import("../../api/admin/adminApi")).default;
      const data = getAgencyUpdateData(field, value);
      const resp = await adminApi.adminUpdateAgency(agencyId, data);
      if (resp?.status === 200) {
        setAgency((prev) =>
          prev
            ? {
                ...prev,
                ...(field === "monthly_fee" && { monthlyFee: value }),
                ...(field === "overage_fee" && { overageFee: value }),
                ...(field === "monthly_credits" && { monthlyCredits: value }),
                ...(field === "plan_type" && { planType: value }),
              }
            : prev
        );
      }
    } finally {
      setAdminUpdatingAgency(false);
    }
  };

  // --- Handlers ---
  const goToAgencyList = () => navigate(`/agencies`);

  const handleClick = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((el) => el !== index) : [...prev, index]
    );
  };

  const handleDeleteUser = async (userId: string, teamName: string) => {
    try {
      const adminApi = (await import("../../api/admin/adminApi")).default;
      const resp = await adminApi.adminDeleteUser(userId);
      if (resp?.status === 200) {
        await fetchAgencyUsersInfo(totalsMonth);
      } else {
        window.alert("Failed to delete user. Please try again.");
      }
    } catch (e) {
      window.alert("Failed to delete user. Please try again.");
    } finally {
      setShowConfirmDeleteUserModal(false);
      setUserToDelete(null);
    }
  };

  const handleExportCSV = () => {
    if (!filteredRuns) return;
    const dataToExport = filteredRuns.map((run, idx) => ({
      index: idx + 1,
      generationTime: run.generationTime.toString(),
      status: run.status,
      taskType: getRunTaskTypeLabel(run.instanceIds),
      username: run.user.username,
      email: run.user.email,
    }));
    const csv = arrayToCSV(dataToExport, columns);
    downloadCSV(csv, "agency_runs.csv");
  };

  const handleExportAgencyUsersCSV = () => {
    if (!agencyUsersInfo) return;
    const summaryRows = [
      "Agency Totals (summary),,,,,,,,,,",
      "Total Policies:,Total Comparisons:,Total Proposals:,Total Contract Reviews:,Total Gap Analyses:,Total Single Policy Analyses:,Total SOV:,Total Loss Runs:,Total Chats:,",
      `${agencyUsersInfo.total_policies ?? 0},${agencyUsersInfo.total_comparisons ?? 0},${agencyUsersInfo.total_proposals ?? 0},${agencyUsersInfo.total_contract_reviews ?? 0},${agencyUsersInfo.total_gap_analyses ?? 0},${agencyUsersInfo.total_single_policy_analyses ?? 0},${agencyUsersInfo.total_sov ?? 0},${agencyUsersInfo.total_loss_runs ?? 0},${agencyUsersInfo.total_chats ?? 0},`,
      ",,,,,,,,,,",
    ];
    const usersHeader = [
      "Users activity,,,,,,,,,,,,,,,",
      "email,first name,last name,role,total policies,total comparisons,total proposals,total contract reviews,total gap analyses,total single policy analyses,total sov,total loss runs,total chats,is admin,is test account,last activity",
    ];
    const usersRows =
      agencyUsersInfo.teams_list
        ?.flatMap((teamObj) => {
          const team = Object.values(teamObj)[0] as { users_info?: any[] };
          return team && Array.isArray(team.users_info)
            ? team.users_info.map((u) =>
                [
                  u.email,
                  u.user_first_name,
                  u.user_last_name,
                  u.role ?? "",
                  u.num_policies,
                  u.num_comparisons,
                  u.num_proposals,
                  u.num_contract_reviews ?? 0,
                  u.num_gap_analyses ?? 0,
                  u.num_single_policy_analyses ?? 0,
                  u.num_sov ?? 0,
                  u.num_loss_runs ?? 0,
                  u.num_chats ?? 0,
                  u.is_admin ? "Yes" : "No",
                  u.is_test_account === null || u.is_test_account === undefined
                    ? "Not Set"
                    : u.is_test_account
                      ? "Yes"
                      : "No",
                  u.last_activity ?? "",
                ]
                  .map(escapeCsvField)
                  .join(",")
              )
            : [];
        })
        .join("\r\n") || "";
    const csv = [...summaryRows, ...usersHeader, usersRows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agency_users_info.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // --- Month Select ---
  const getMonthOptions = () => {
    const startYear = 2024;
    const now = new Date();
    const endYear = now.getFullYear();
    const endMonth = now.getMonth() + 1;
    const groups: any[] = [];
    for (let year = startYear; year <= endYear; year++) {
      let months: any[] = [];
      const monthEnd = year === endYear ? endMonth : 12;
      for (let m = 1; m <= monthEnd; m++) {
        months.push({
          value: `${year}-${m.toString().padStart(2, "0")}-01`,
          label: `${MONTHS[m - 1]}`,
        });
      }
      months = months.reverse();
      groups.push({
        label: String(year),
        options: months,
      });
    }
    return groups.reverse();
  };
  const formatGroupLabel = (data: any) => (
    <div style={{ fontWeight: 600, fontSize: 18, margin: "8px 0 0 0" }}>{data.label}</div>
  );
  const ALL_OPTION = { value: "all", label: "All time" };
  const monthOptions = getMonthOptions();
  const flatOptions = monthOptions.flatMap((g) => g.options);
  const selectedMonth =
    totalsMonth === "all" ? ALL_OPTION : flatOptions.find((opt) => opt.value === totalsMonth);
  const allOptions = [ALL_OPTION, ...(monthOptions as any)];

  // --- Render ---
  if (!agencyId) {
    return (
      <div>
        <h1>Error</h1>
        <p>No agency ID provided in the URL.</p>
      </div>
    );
  }
  if (isError) {
    return <p>Failed to load agency runs.</p>;
  }

  return (
    <Page>
      {/* Section 1: Header with Title and Logout */}
      <section
        className="agency-page-section"
        style={{
          width: "100%",
          alignSelf: "stretch",
          boxSizing: "border-box" as const,
          padding: "0 10vw",
          marginBottom: 24,
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            paddingBottom: 8,
            flexWrap: "wrap" as const,
            gap: 8,
          }}
        >
          <h1
            className="agency-page-header"
            style={{
              margin: 0,
              fontSize: "clamp(32px, 6vw, 49px)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: "1 1 auto",
              minWidth: 0,
              fontWeight: 600,
              color: "#1a1a1a",
            }}
          >
            Agency: {agencyName}
          </h1>
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap" as const,
            }}
          >
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#6b7280",
                color: "#fff",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "background-color 0.2s ease",
              }}
              onClick={goToAgencyList}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#6b7280";
              }}
            >
              Back to Home
            </button>
            <LogoutButton />
          </div>
        </div>
      </section>

      {/* Section 2: Main Content with Table */}
      <section
        className="agency-page-section"
        style={{
          width: "100%",
          alignSelf: "stretch",
          boxSizing: "border-box" as const,
        }}
      >
        <div className="agency-page-table-center" style={styles.tableCenter}>
          <div style={{ padding: "0 10vw" }}>
            {/* Action Bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div
                className="agency-page-buttons"
                style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
              >
                <Button
                  text="Create access token"
                  handleClick={() => setShowCreateTokenModal(true)}
                  $secondary
                />
                <Button text="Invite users" handleClick={() => setShowInviteUsersModal(true)} />
                <Button text="Filter" handleClick={() => setFilter((prev) => !prev)} />
              </div>
              <button
                style={{
                  background: "#4caf50",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 20px",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
                onClick={handleExportAgencyUsersCSV}
                disabled={isLoadingUsersInfo}
              >
                Export CSV
              </button>
            </div>

            {/* Info Cards Row */}
            {(() => {
              const cardStyle: React.CSSProperties = {
                background: "#fff",
                borderRadius: 8,
                border: "1px solid #e0e0e0",
                padding: "16px 20px",
                flex: "1 1 0",
                minWidth: 280,
              };
              const sectionLabel: React.CSSProperties = {
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#7986cb",
                marginBottom: 12,
              };
              const rowStyle: React.CSSProperties = {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "5px 0",
                fontSize: 14,
              };
              const valStyle: React.CSSProperties = { fontWeight: 600, color: "#1a1a1a" };
              const lblStyle: React.CSSProperties = {
                color: "#666",
                flexShrink: 0,
                marginRight: 12,
              };

              return (
                <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                  {/* Card: Account Settings */}
                  <div style={cardStyle}>
                    <div style={sectionLabel}>Account Settings</div>
                    {(() => {
                      const r: React.CSSProperties = {
                        display: "flex",
                        alignItems: "center",
                        height: 32,
                        fontSize: 14,
                      };
                      const lbl: React.CSSProperties = { color: "#666", flex: "1 1 auto" };
                      const val: React.CSSProperties = { fontWeight: 600, color: "#1a1a1a" };
                      const clickVal: React.CSSProperties = {
                        ...val,
                        cursor: "pointer",
                        borderBottom: "1px dashed #ccc",
                      };

                      const userCount = (() => {
                        const allUsers = teamsList.flatMap((teamObj) => {
                          const teamId = Object.keys(teamObj)[0];
                          return teamObj[teamId]?.users_info ?? [];
                        });
                        const seen = new Set<string>();
                        return allUsers.filter((u) => {
                          const key = u.user_id || `email:${u.email}`;
                          if (seen.has(key)) return false;
                          seen.add(key);
                          return true;
                        }).length;
                      })();

                      const editableField = (
                        fieldKey: string,
                        displayValue: string,
                        onSave: (v: string) => Promise<void>
                      ) => {
                        if (editingSettingsField === fieldKey) {
                          return (
                            <EditableTableText
                              defaultValue={displayValue}
                              notEditable={false}
                              onChange={() => {}}
                              onSubmitChange={async (v) => {
                                await onSave(v);
                                setEditingSettingsField(null);
                              }}
                              $noPadding
                              maxWidth="120px"
                              minWidth="60px"
                            />
                          );
                        }
                        return (
                          <span
                            style={clickVal}
                            onClick={() => setEditingSettingsField(fieldKey)}
                            title="Click to edit"
                          >
                            {displayValue || "—"}
                          </span>
                        );
                      };

                      return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div style={r}>
                            <span style={lbl}>Users</span>
                            <span style={val}>{userCount}</span>
                          </div>
                          {agency && (
                            <>
                              <div style={r}>
                                <span style={lbl}>Status</span>
                                <div style={{ width: 110 }}>
                                  <Select
                                    value={{
                                      value: displayStatus,
                                      label: agency.unknownStatus ?? statusLabel(displayStatus),
                                    }}
                                    options={AGENCY_STATUSES.map((s) => ({
                                      value: s,
                                      label: statusLabel(s),
                                    }))}
                                    isDisabled={adminUpdatingAgency}
                                    isSearchable={false}
                                    isClearable={false}
                                    menuPlacement="auto"
                                    onChange={async (opt) => {
                                      if (!opt) return;
                                      if (opt.value === displayStatus) return;
                                      await handleChangeStatus(opt.value);
                                    }}
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: 28,
                                        height: 28,
                                        fontSize: 13,
                                        borderColor: "#e0e0e0",
                                      }),
                                      valueContainer: (base) => ({
                                        ...base,
                                        height: 28,
                                        padding: "0 8px",
                                      }),
                                      input: (base) => ({ ...base, margin: 0 }),
                                      indicatorsContainer: (base) => ({ ...base, height: 28 }),
                                      menu: (base) => ({ ...base, fontSize: 13 }),
                                    }}
                                  />
                                </div>
                              </div>
                              <div style={r}>
                                <span style={lbl}>Plan</span>
                                {editableField("plan_type", agency.planType ?? "", async (v) => {
                                  if (v !== agency.planType)
                                    await handleAgencyFieldEdit("plan_type", v);
                                })}
                              </div>
                              {agency.isActive && (
                                <>
                                  <div style={r}>
                                    <span style={lbl}>Monthly fee</span>
                                    {editableField(
                                      "monthly_fee",
                                      agency.monthlyFee != null ? String(agency.monthlyFee) : "",
                                      async (v) => {
                                        const n = v === "" ? null : Number(v);
                                        if (n !== agency.monthlyFee)
                                          await handleAgencyFieldEdit("monthly_fee", n);
                                      }
                                    )}
                                  </div>
                                  <div style={r}>
                                    <span style={lbl}>Overage fee</span>
                                    {editableField(
                                      "overage_fee",
                                      agency.overageFee != null ? String(agency.overageFee) : "",
                                      async (v) => {
                                        const n = v === "" ? null : Number(v);
                                        if (n !== agency.overageFee)
                                          await handleAgencyFieldEdit("overage_fee", n);
                                      }
                                    )}
                                  </div>
                                  <div style={r}>
                                    <span style={lbl}>Monthly credits</span>
                                    {editableField(
                                      "monthly_credits",
                                      agency.monthlyCredits != null
                                        ? String(agency.monthlyCredits)
                                        : "",
                                      async (v) => {
                                        const n = v === "" ? null : Number(v);
                                        if (n !== agency.monthlyCredits)
                                          await handleAgencyFieldEdit("monthly_credits", n);
                                      }
                                    )}
                                  </div>
                                  <div style={r}>
                                    <span style={lbl}>Allowed users</span>
                                    <span style={val}>
                                      {agency.allowedUsers != null ? agency.allowedUsers : "—"}
                                    </span>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Card: Usage Totals */}
                  <div style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                      }}
                    >
                      <div style={sectionLabel}>Usage Totals</div>
                      <div style={{ width: 160 }}>
                        <Select
                          value={
                            selectedMonth && selectedMonth.value !== "all"
                              ? {
                                  ...selectedMonth,
                                  label: `${selectedMonth.label} ${selectedMonth.value.split("-")[0]}`,
                                }
                              : selectedMonth || null
                          }
                          onChange={(opt) => setTotalsMonth(opt?.value || "")}
                          options={allOptions}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: 28,
                              height: 28,
                              fontSize: 12,
                              borderColor: "#e0e0e0",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              height: 28,
                              padding: "0 6px",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: 28,
                            }),
                          }}
                          isSearchable={false}
                          isClearable={false}
                          maxMenuHeight={200}
                          placeholder="Select month"
                          formatGroupLabel={formatGroupLabel}
                        />
                      </div>
                    </div>
                    {[
                      ["Policies", agencyUsersInfo?.total_policies ?? 0],
                      ["Comparisons", agencyUsersInfo?.total_comparisons ?? 0],
                      ["Proposals", agencyUsersInfo?.total_proposals ?? 0],
                      ["Contract Reviews", agencyUsersInfo?.total_contract_reviews ?? 0],
                      ["Gap Analyses", agencyUsersInfo?.total_gap_analyses ?? 0],
                      [
                        "Single Policy Analyses",
                        agencyUsersInfo?.total_single_policy_analyses ?? 0,
                      ],
                      ["SOV", agencyUsersInfo?.total_sov ?? 0],
                      ["Loss Runs", agencyUsersInfo?.total_loss_runs ?? 0],
                      ["Chats", agencyUsersInfo?.total_chats ?? 0],
                    ].map(([label, count]) => (
                      <div key={label as string} style={rowStyle}>
                        <span style={lblStyle}>{label}</span>
                        <span style={valStyle}>{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Teams & View Toggle Bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#555",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Team:
                </span>
                <Button
                  key="all_teams"
                  text="All Teams"
                  handleClick={() => setSelectedTeamId("all_teams")}
                  $bold={selectedTeamId === "all_teams"}
                  $background={selectedTeamId === "all_teams" ? "#e0e7fa" : "#f5f5f5"}
                  $borderless
                />
                {teamIds.map((teamId) => (
                  <Button
                    key={teamId}
                    text={teamIdToName[teamId]}
                    handleClick={() => setSelectedTeamId(teamId)}
                    $bold={selectedTeamId === teamId}
                    $background={selectedTeamId === teamId ? "#e0e7fa" : "#f5f5f5"}
                    $borderless
                  />
                ))}
                <EditableTableText
                  notEditable={false}
                  onChange={() => {}}
                  onSubmitChange={async (newValue: string) => {
                    if (!newValue.trim() || newValue.trim().length < 2) return;
                    if (!agencyId) return;
                    try {
                      const adminApi = (await import("../../api/admin/adminApi")).default;
                      const resp = await adminApi.adminCreateTeam(agencyId, newValue.trim());
                      if (resp?.status === 200 || resp?.status === 201) {
                        await fetchAgencyUsersInfo(totalsMonth);
                        const updatedTeamsList = agencyUsersInfo?.teams_list ?? [];
                        if (updatedTeamsList.length > 0) {
                          const lastTeamObj = updatedTeamsList[updatedTeamsList.length - 1];
                          const lastTeamId = Object.keys(lastTeamObj)[0];
                          setSelectedTeamId(lastTeamId);
                        }
                      }
                    } catch (e) {}
                  }}
                  placeholder="+ Add Team"
                  $noPadding
                  $clearOnSubmit
                  maxWidth="150px"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#f0f0f0",
                  borderRadius: 6,
                  padding: 3,
                }}
              >
                <button
                  onClick={() => setIsActivityTable(true)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    background: isActivityTable ? "#3949ab" : "transparent",
                    color: isActivityTable ? "#fff" : "#555",
                    transition: "all 0.15s ease",
                  }}
                >
                  User Activity
                </button>
                <button
                  onClick={() => setIsActivityTable(false)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    background: !isActivityTable ? "#3949ab" : "transparent",
                    color: !isActivityTable ? "#fff" : "#555",
                    transition: "all 0.15s ease",
                  }}
                >
                  Runs
                </button>
              </div>
            </div>
          </div>
          {/* User Activity Table */}
          {isActivityTable && (
            <div style={{ width: "100%" }}>
              <table
                style={{ borderCollapse: "collapse", width: "100%", minWidth: 1400, fontSize: 13 }}
              >
                <thead>
                  <tr>
                    <th
                      colSpan={4}
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: 13,
                        backgroundColor: "#3949ab",
                        color: "#fff",
                        borderRight: "2px solid rgba(255,255,255,0.3)",
                      }}
                    >
                      User Info
                    </th>
                    <th
                      colSpan={9}
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: 13,
                        backgroundColor: "#3949ab",
                        color: "#fff",
                        borderRight: "2px solid rgba(255,255,255,0.3)",
                      }}
                    >
                      Activity
                    </th>
                    <th
                      colSpan={4}
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: 13,
                        backgroundColor: "#3949ab",
                        color: "#fff",
                        borderRight: "2px solid rgba(255,255,255,0.3)",
                      }}
                    >
                      Settings
                    </th>
                    <th
                      colSpan={2}
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: 13,
                        backgroundColor: "#3949ab",
                        color: "#fff",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                  {(() => {
                    const sh: React.CSSProperties = {
                      padding: "6px 10px",
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: 12,
                      backgroundColor: "#5c6bc0",
                      color: "#fff",
                      whiteSpace: "nowrap",
                    };
                    const gb: React.CSSProperties = {
                      borderRight: "2px solid rgba(255,255,255,0.3)",
                    };
                    return (
                      <tr>
                        <th style={{ ...sh, textAlign: "left", minWidth: 120 }}>First Name</th>
                        <th style={{ ...sh, textAlign: "left", minWidth: 100 }}>Last Name</th>
                        <th style={{ ...sh, textAlign: "left", minWidth: 180 }}>Email</th>
                        <th style={{ ...sh, textAlign: "left", minWidth: 110, ...gb }}>Role</th>
                        <th style={{ ...sh, minWidth: 55 }}>Policies</th>
                        <th style={{ ...sh, minWidth: 55 }}>Comps</th>
                        <th style={{ ...sh, minWidth: 60 }}>Proposals</th>
                        <th style={{ ...sh, minWidth: 55 }}>Reviews</th>
                        <th style={{ ...sh, minWidth: 45 }}>Gap</th>
                        <th style={{ ...sh, minWidth: 40 }}>SPA</th>
                        <th style={{ ...sh, minWidth: 40 }}>SOV</th>
                        <th style={{ ...sh, minWidth: 50 }}>Loss Runs</th>
                        <th style={{ ...sh, minWidth: 40, ...gb }}>Chats</th>
                        <th style={{ ...sh, minWidth: 160 }}>Agency</th>
                        <th style={{ ...sh, minWidth: 80 }}>Admin</th>
                        <th style={{ ...sh, minWidth: 100 }}>Test</th>
                        <th style={{ ...sh, minWidth: 55, ...gb }}>Last Active</th>
                        <th style={{ ...sh, minWidth: 30 }}></th>
                        <th style={{ ...sh, minWidth: 50 }}></th>
                      </tr>
                    );
                  })()}
                </thead>
                <tbody>
                  {isLoadingUsersInfo ? (
                    <tr>
                      <td
                        colSpan={19}
                        style={{
                          textAlign: "center",
                          padding: 24,
                          borderBottom: "1px solid #e8e8e8",
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>Loading agency users info...</div>
                        <Spinner />
                      </td>
                    </tr>
                  ) : agencyUsersInfo ? (
                    selectedUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={19}
                          style={{
                            textAlign: "center",
                            padding: 16,
                            borderBottom: "1px solid #e8e8e8",
                            color: "#888",
                          }}
                        >
                          No users in this team
                        </td>
                      </tr>
                    ) : (
                      selectedUsers.map((user, idx) => {
                        const testValue =
                          user.user_id &&
                          Object.prototype.hasOwnProperty.call(testAccountOverrides, user.user_id)
                            ? testAccountOverrides[user.user_id]
                            : (user.is_test_account ?? null);
                        const testLabel =
                          testValue === null || testValue === undefined
                            ? "Not Set"
                            : testValue
                              ? "Yes"
                              : "No";
                        const c: React.CSSProperties = {
                          padding: "6px 10px",
                          borderBottom: "1px solid #e8e8e8",
                          textAlign: "center",
                          fontSize: 13,
                        };
                        const gb: React.CSSProperties = { borderRight: "2px solid #e0e0e0" };
                        return (
                          <tr
                            key={user.user_id || idx}
                            style={{ backgroundColor: idx % 2 === 0 ? "#f8f9ff" : "#fff" }}
                          >
                            <td style={{ ...c, textAlign: "left" }}>
                              <EditableTableText
                                defaultValue={user.user_first_name ? user.user_first_name : "-"}
                                notEditable={false}
                                onChange={() => {}}
                                onSubmitChange={async (newValue: string) => {
                                  if (newValue === user.user_first_name) return;
                                  setAdminUpdatingUserId(user.user_id);
                                  try {
                                    const adminApi = (await import("../../api/admin/adminApi"))
                                      .default;
                                    const data = {
                                      first_name: newValue,
                                      last_name: null,
                                      email: null,
                                      is_admin: null,
                                      test_account: null,
                                    };
                                    const resp = await adminApi.adminUpdateUser(user.user_id, data);
                                    if (resp.status === 200) {
                                      setAgencyUsersInfo((prev: any) => {
                                        if (!prev) return prev;
                                        const newTeamsList = prev.teams_list.map((teamObj: any) => {
                                          const teamKey = Object.keys(teamObj)[0];
                                          const team = { ...teamObj[teamKey] };
                                          if (team.users_info) {
                                            team.users_info = team.users_info.map((u: any) =>
                                              u.user_id === user.user_id
                                                ? { ...u, user_first_name: newValue }
                                                : u
                                            );
                                          }
                                          return { [teamKey]: team };
                                        });
                                        return { ...prev, teams_list: newTeamsList };
                                      });
                                    }
                                  } finally {
                                    setAdminUpdatingUserId(null);
                                  }
                                }}
                                placeholder="First Name"
                                minWidth="125px"
                              />
                            </td>
                            <td style={{ ...c, textAlign: "left" }}>
                              <EditableTableText
                                defaultValue={user.user_last_name ? user.user_last_name : "-"}
                                notEditable={false}
                                onChange={() => {}}
                                onSubmitChange={async (newValue: string) => {
                                  if (newValue === user.user_last_name) return;
                                  setAdminUpdatingUserId(user.user_id);
                                  try {
                                    const adminApi = (await import("../../api/admin/adminApi"))
                                      .default;
                                    const data = {
                                      first_name: null,
                                      last_name: newValue,
                                      email: null,
                                      is_admin: null,
                                      test_account: null,
                                    };
                                    const resp = await adminApi.adminUpdateUser(user.user_id, data);
                                    if (resp.status === 200) {
                                      setAgencyUsersInfo((prev: any) => {
                                        if (!prev) return prev;
                                        const newTeamsList = prev.teams_list.map((teamObj: any) => {
                                          const teamKey = Object.keys(teamObj)[0];
                                          const team = { ...teamObj[teamKey] };
                                          if (team.users_info) {
                                            team.users_info = team.users_info.map((u: any) =>
                                              u.user_id === user.user_id
                                                ? { ...u, user_last_name: newValue }
                                                : u
                                            );
                                          }
                                          return { [teamKey]: team };
                                        });
                                        return { ...prev, teams_list: newTeamsList };
                                      });
                                    }
                                  } finally {
                                    setAdminUpdatingUserId(null);
                                  }
                                }}
                                placeholder="Last Name"
                                maxWidth="120px"
                              />
                            </td>
                            <td style={{ ...c, textAlign: "left" }}>
                              <EditableTableText
                                defaultValue={user.email}
                                notEditable={false}
                                onChange={() => {}}
                                onSubmitChange={async (newValue: string) => {
                                  if (newValue === user.email) return;
                                  setAdminUpdatingUserId(user.user_id);
                                  try {
                                    const adminApi = (await import("../../api/admin/adminApi"))
                                      .default;
                                    const data = {
                                      first_name: null,
                                      last_name: null,
                                      email: newValue,
                                      is_admin: null,
                                      test_account: null,
                                    };
                                    const resp = await adminApi.adminUpdateUser(user.user_id, data);
                                    if (resp.status === 200) {
                                      setAgencyUsersInfo((prev: any) => {
                                        if (!prev) return prev;
                                        const newTeamsList = prev.teams_list.map((teamObj: any) => {
                                          const teamKey = Object.keys(teamObj)[0];
                                          const team = { ...teamObj[teamKey] };
                                          if (team.users_info) {
                                            team.users_info = team.users_info.map((u: any) =>
                                              u.user_id === user.user_id
                                                ? { ...u, email: newValue }
                                                : u
                                            );
                                          }
                                          return { [teamKey]: team };
                                        });
                                        return { ...prev, teams_list: newTeamsList };
                                      });
                                    }
                                  } finally {
                                    setAdminUpdatingUserId(null);
                                  }
                                }}
                                placeholder="Email"
                                maxWidth="260px"
                              />
                            </td>
                            <td style={{ ...c, textAlign: "left", ...gb }}>{user.role || "-"}</td>
                            <td style={c}>{user.num_policies}</td>
                            <td style={c}>{user.num_comparisons}</td>
                            <td style={c}>{user.num_proposals}</td>
                            <td style={c}>{user.num_contract_reviews ?? 0}</td>
                            <td style={c}>{user.num_gap_analyses ?? 0}</td>
                            <td style={c}>{user.num_single_policy_analyses ?? 0}</td>
                            <td style={c}>{user.num_sov ?? 0}</td>
                            <td style={c}>{user.num_loss_runs ?? 0}</td>
                            <td style={{ ...c, ...gb }}>{user.num_chats ?? 0}</td>
                            <td style={c}>
                              <Select
                                value={
                                  agencyOptions.find((o) => o.value === agencyId) ??
                                  (agency
                                    ? {
                                        value: agency.agencyId,
                                        label: agency.name || agency.agencyId,
                                      }
                                    : null)
                                }
                                options={agencyOptions}
                                isDisabled={adminUpdatingUserId === user.user_id}
                                onChange={async (opt) => {
                                  if (!opt || opt.value === agencyId || !user.user_id) return;
                                  if (
                                    !window.confirm(
                                      `Move ${user.email || user.user_id} to "${opt.label}"? They will be removed from this agency and their self-service flag cleared.`
                                    )
                                  )
                                    return;
                                  setAdminUpdatingUserId(user.user_id);
                                  try {
                                    const adminApi = (await import("../../api/admin/adminApi"))
                                      .default;
                                    const resp = await adminApi.adminUpdateUser(user.user_id, {
                                      agency_id: opt.value,
                                      is_self_serviced: false,
                                    });
                                    if (resp.status === 200) {
                                      setAgencyUsersInfo((prev: any) => {
                                        if (!prev) return prev;
                                        const newTeamsList = prev.teams_list.map((teamObj: any) => {
                                          const teamKey = Object.keys(teamObj)[0];
                                          const team = { ...teamObj[teamKey] };
                                          if (team.users_info) {
                                            team.users_info = team.users_info.filter(
                                              (u: any) => u.user_id !== user.user_id
                                            );
                                          }
                                          return { [teamKey]: team };
                                        });
                                        return { ...prev, teams_list: newTeamsList };
                                      });
                                    } else {
                                      window.alert("Failed to move user. Please try again.");
                                    }
                                  } catch (e) {
                                    window.alert("Failed to move user. Please try again.");
                                  } finally {
                                    setAdminUpdatingUserId(null);
                                  }
                                }}
                                styles={{
                                  container: (base) => ({ ...base, minWidth: 160, width: "100%" }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: 28,
                                    fontSize: 14,
                                    width: "100%",
                                    boxSizing: "border-box",
                                  }),
                                  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
                                  input: (base) => ({ ...base, margin: 0 }),
                                  menu: (base) => ({ ...base, fontSize: 14, zIndex: 9999 }),
                                }}
                                menuPortalTarget={selectMenuPortalTarget}
                                menuPosition="fixed"
                                isClearable={false}
                                menuPlacement="auto"
                              />
                            </td>
                            <td style={c}>
                              <Select
                                value={{
                                  value: user.is_admin ? "Yes" : "No",
                                  label: user.is_admin ? "Yes" : "No",
                                }}
                                options={[
                                  { value: "Yes", label: "Yes" },
                                  { value: "No", label: "No" },
                                ]}
                                isDisabled={adminUpdatingUserId === user.user_id}
                                onChange={async (opt) => {
                                  if (!opt) return;
                                  const newVal = opt.value === "Yes";
                                  if (newVal === user.is_admin) return;
                                  setAdminUpdatingUserId(user.user_id);
                                  try {
                                    const adminApi = (await import("../../api/admin/adminApi"))
                                      .default;
                                    const data = {
                                      first_name: null,
                                      last_name: null,
                                      email: null,
                                      is_admin: newVal,
                                      test_account: null,
                                    };
                                    const resp = await adminApi.adminUpdateUser(user.user_id, data);
                                    if (resp.status === 200) {
                                      setAgencyUsersInfo((prev: any) => {
                                        if (!prev) return prev;
                                        const newTeamsList = prev.teams_list.map((teamObj: any) => {
                                          const teamKey = Object.keys(teamObj)[0];
                                          const team = { ...teamObj[teamKey] };
                                          if (team.users_info) {
                                            team.users_info = team.users_info.map((u: any) =>
                                              u.user_id === user.user_id
                                                ? { ...u, is_admin: newVal }
                                                : u
                                            );
                                          }
                                          return { [teamKey]: team };
                                        });
                                        return { ...prev, teams_list: newTeamsList };
                                      });
                                    }
                                  } finally {
                                    setAdminUpdatingUserId(null);
                                  }
                                }}
                                styles={{
                                  container: (base) => ({
                                    ...base,
                                    minWidth: 100,
                                    width: "100%",
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: 28,
                                    height: 28,
                                    fontSize: 14,
                                    width: "100%",
                                    boxSizing: "border-box",
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    height: 28,
                                    padding: "0 6px",
                                  }),
                                  input: (base) => ({
                                    ...base,
                                    margin: 0,
                                  }),
                                  indicatorsContainer: (base) => ({
                                    ...base,
                                    height: 28,
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    fontSize: 14,
                                    zIndex: 9999,
                                  }),
                                }}
                                menuPortalTarget={selectMenuPortalTarget}
                                menuPosition="fixed"
                                isSearchable={false}
                                isClearable={false}
                                menuPlacement="auto"
                              />
                            </td>
                            <td style={c}>
                              <div
                                title="When set to Not Set, the system infers test accounts using email pattern checks."
                                style={{ position: "relative", zIndex: 2 }}
                              >
                                <Select
                                  value={{ value: testLabel, label: testLabel }}
                                  options={[
                                    { value: "Yes", label: "Yes" },
                                    { value: "No", label: "No" },
                                    { value: "Not Set", label: "Not Set" },
                                  ]}
                                  isDisabled={adminUpdatingUserId === user.user_id}
                                  onChange={async (opt) => {
                                    if (!opt) return;
                                    const newVal =
                                      opt.value === "Yes"
                                        ? true
                                        : opt.value === "No"
                                          ? false
                                          : null;
                                    const currentVal = testValue ?? null;
                                    if (newVal === currentVal) return;
                                    if (!user.user_id) return;
                                    setAdminUpdatingUserId(user.user_id);
                                    try {
                                      const adminApi = (await import("../../api/admin/adminApi"))
                                        .default;
                                      const data = {
                                        first_name: null,
                                        last_name: null,
                                        email: null,
                                        is_admin: null,
                                        test_account: newVal,
                                      };
                                      const resp = await adminApi.adminUpdateUser(
                                        user.user_id,
                                        data
                                      );
                                      if (resp.status === 200) {
                                        setAgencyUsersInfo((prev: any) => {
                                          if (!prev) return prev;
                                          const newTeamsList = prev.teams_list.map(
                                            (teamObj: any) => {
                                              const teamKey = Object.keys(teamObj)[0];
                                              const team = { ...teamObj[teamKey] };
                                              if (team.users_info) {
                                                team.users_info = team.users_info.map((u: any) =>
                                                  u.user_id === user.user_id
                                                    ? { ...u, is_test_account: newVal }
                                                    : u
                                                );
                                              }
                                              return { [teamKey]: team };
                                            }
                                          );
                                          return { ...prev, teams_list: newTeamsList };
                                        });
                                        setTestAccountOverrides((prev) => ({
                                          ...prev,
                                          [user.user_id]: newVal,
                                        }));
                                      }
                                    } finally {
                                      setAdminUpdatingUserId(null);
                                    }
                                  }}
                                  styles={{
                                    container: (base) => ({
                                      ...base,
                                      minWidth: 140,
                                      width: "100%",
                                    }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: 28,
                                      height: 28,
                                      fontSize: 14,
                                      width: "100%",
                                      boxSizing: "border-box",
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      height: 28,
                                      padding: "0 6px",
                                    }),
                                    input: (base) => ({
                                      ...base,
                                      margin: 0,
                                    }),
                                    indicatorsContainer: (base) => ({
                                      ...base,
                                      height: 28,
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      fontSize: 14,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  menuPortalTarget={selectMenuPortalTarget}
                                  menuPosition="fixed"
                                  isSearchable={false}
                                  isClearable={false}
                                  menuPlacement="auto"
                                />
                              </div>
                            </td>
                            <td style={{ ...c, ...gb }}>
                              {user.last_activity ? `${user.last_activity}d` : "-"}
                            </td>
                            <td style={c}>
                              <Button
                                iconLeft={Trash}
                                handleClick={() => {
                                  setUserToDelete({
                                    user_id: user.user_id,
                                    team_name: selectedTeamId,
                                  });
                                  setShowConfirmDeleteUserModal(true);
                                }}
                              />
                            </td>
                            <td style={c}>
                              <Button
                                text="Login"
                                handleClick={async () => {
                                  if (user.user_id) {
                                    await handleAdminLoginAsUser(user.user_id);
                                  }
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })
                    )
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
          {/* Runs Table */}
          {!isActivityTable && (
            <div style={{ width: "100%" }}>
              <table
                style={{ borderCollapse: "collapse", width: "100%", minWidth: 800, fontSize: 13 }}
              >
                <thead>
                  {(() => {
                    const sh: React.CSSProperties = {
                      padding: "10px 12px",
                      backgroundColor: "#3949ab",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    };
                    return (
                      <tr>
                        <th style={{ ...sh, minWidth: 30 }}>#</th>
                        <th style={{ ...sh, minWidth: 180, textAlign: "left" }}>Generation Time</th>
                        <th style={{ ...sh, minWidth: 60 }}>Status</th>
                        <th style={{ ...sh, minWidth: 80 }}>Task Type</th>
                        <th style={{ ...sh, minWidth: 80 }}>User</th>
                        <th style={{ ...sh, minWidth: 150, textAlign: "left" }}>Email</th>
                      </tr>
                    );
                  })()}
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          textAlign: "center",
                          padding: 24,
                          borderBottom: "1px solid #e8e8e8",
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>Loading agency runs...</div>
                        <Spinner />
                      </td>
                    </tr>
                  ) : (
                    pagedRuns &&
                    pagedRuns.map((run, index) => {
                      const rc: React.CSSProperties = {
                        padding: "8px 10px",
                        borderBottom: "1px solid #e8e8e8",
                        textAlign: "center",
                        fontSize: 13,
                      };
                      const rowIdx = index + (page - 1) * PAGE_SIZE;
                      return (
                        <React.Fragment key={index + (page - 1) * runsTableColumnWidths.length}>
                          <tr
                            onClick={() => handleClick(rowIdx)}
                            style={{
                              backgroundColor: index % 2 === 0 ? "#f8f9ff" : "#fff",
                              cursor: "pointer",
                            }}
                          >
                            <td style={rc}>{rowIdx + 1}</td>
                            <td style={{ ...rc, textAlign: "left", whiteSpace: "nowrap" }}>
                              {run.generationTime.toString()}
                            </td>
                            <td style={rc}>{run.status}</td>
                            <td style={rc}>{getRunTaskTypeLabel(run.instanceIds)}</td>
                            <td style={rc}>{run.user.username}</td>
                            <td style={{ ...rc, textAlign: "left" }}>{run.user.email}</td>
                          </tr>
                          {expandedRows.includes(rowIdx) && (
                            <tr style={{ backgroundColor: "#f0f2ff" }}>
                              <td
                                colSpan={6}
                                style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e8" }}
                              >
                                <div style={{ textAlign: "left" }}>
                                  <ul>
                                    <li>Carrier: {run.result.carrier}</li>
                                    <li>Form Schedule: {run.result.formSchedule}</li>
                                    <li>Key Info: {run.result.keyInfo}</li>
                                    <li>Policy Period: {run.result.policyPeriod}</li>
                                    <li>Run Time: {run.result.runTime}</li>
                                    <br />
                                    <li>
                                      Types:{" "}
                                      {run.result.types &&
                                        run.result.types.map((type, i) => <li key={i}>{type}</li>)}
                                    </li>
                                  </ul>
                                  <div style={styles.expandedCell}></div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
              {!isLoading && totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "16px 0",
                  }}
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: "6px 12px",
                      marginRight: 8,
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      background: page === 1 ? "#eee" : "#fff",
                      cursor: page === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Prev
                  </button>
                  <span style={{ margin: "0 8px" }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      padding: "6px 12px",
                      marginLeft: 8,
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      background: page === totalPages ? "#eee" : "#fff",
                      cursor: page === totalPages ? "not-allowed" : "pointer",
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      {/* Modals */}
      <CreateAccessTokenModal
        open={showCreateTokenModal}
        onCancel={() => setShowCreateTokenModal(false)}
        onCreate={async (accessToken: string) => {
          if (!agencyId) return;
          try {
            const adminApi = (await import("../../api/admin/adminApi")).default;
            await adminApi.adminCreateAccessToken(agencyId, accessToken);
          } catch (e) {}
          setShowCreateTokenModal(false);
        }}
      />
      <InviteUsersModal
        open={showInviteUsersModal}
        onCancel={() => setShowInviteUsersModal(false)}
        onInvite={() => setShowInviteUsersModal(false)}
        agencyId={agencyId}
      />
      <ConfirmDeleteUserModal
        open={showConfirmDeleteUserModal}
        onClose={() => {
          setShowConfirmDeleteUserModal(false);
          setUserToDelete(null);
        }}
        onConfirm={async () => {
          if (userToDelete && typeof userToDelete === "object") {
            await handleDeleteUser(userToDelete.user_id, userToDelete.team_name);
          }
        }}
      />
    </Page>
  );
};

export default AgencyPage;
